import {
  formatMass,
  getItemDefinition,
  stackMassGrams,
  type InventoryActionKind,
  type InventorySlot,
  type ItemStack,
  type PocketRef,
} from "@endclock/shared";
import type { GameInput } from "../game/input.js";
import type { NetworkClient } from "../game/network.js";
import { itemIcon } from "./item-icons.js";
import type { ClientInventorySnapshot, ClientInventoryStore } from "./inventory-store.js";
import { PhysicalCarryUi } from "./physical-carry-ui.js";

type InventoryArea = "PLAYER" | "CONTAINER";
interface Selection {
  readonly area: InventoryArea;
  readonly stackId: string;
}
type QuantityAction = "SPLIT" | "DROP" | "TRANSFER";

export class InventoryUi {
  private readonly root = required<HTMLElement>("#inventory-shell");
  private readonly playerGrid = required<HTMLElement>("#inventory-player-grid");
  private readonly containerPane = required<HTMLElement>("#inventory-container-pane");
  private readonly containerGrid = required<HTMLElement>("#inventory-container-grid");
  private readonly mobileButton = required<HTMLButtonElement>("#inventory-open");
  private readonly closeButton = required<HTMLButtonElement>("#inventory-close");
  private readonly playerMass = required<HTMLElement>("#inventory-player-mass");
  private readonly playerSlots = required<HTMLElement>("#inventory-player-slots");
  private readonly containerTitle = required<HTMLElement>("#inventory-container-title");
  private readonly details = required<HTMLElement>("#inventory-details");
  private readonly splitButton = required<HTMLButtonElement>("#inventory-split");
  private readonly dropButton = required<HTMLButtonElement>("#inventory-drop");
  private readonly transferButton = required<HTMLButtonElement>("#inventory-transfer");
  private readonly transferQuantityButton = required<HTMLButtonElement>("#inventory-transfer-quantity");
  private readonly status = required<HTMLElement>("#inventory-status");
  private readonly quantityDialog = required<HTMLElement>("#inventory-quantity-dialog");
  private readonly quantityTitle = required<HTMLElement>("#inventory-quantity-title");
  private readonly quantityInput = required<HTMLInputElement>("#inventory-quantity");
  private readonly quantityConfirm = required<HTMLButtonElement>("#inventory-quantity-confirm");
  private readonly quantityCancel = required<HTMLButtonElement>("#inventory-quantity-cancel");
  private selection: Selection | null = null;
  private quantityAction: QuantityAction | null = null;
  private requestCounter = 0;
  private previousContainerSession: string | null = null;
  private unsubscribe: (() => void) | undefined;
  private readonly physical: PhysicalCarryUi;

  constructor(
    private readonly store: ClientInventoryStore,
    private readonly network: NetworkClient,
    private readonly input: GameInput,
  ) {
    this.physical = new PhysicalCarryUi(store, network,
      () => this.selection,
      (stackId) => { this.selection = { area: "PLAYER", stackId }; this.render(store.snapshot()); },
      (destination) => this.transferSelected(undefined, destination),
      () => this.input.gameplayActionsEnabled,
      () => this.input.ownsPointerAim);
    this.unsubscribe = store.subscribe((snapshot) => this.render(snapshot));
    this.mobileButton.addEventListener("click", () => this.toggle());
    this.closeButton.addEventListener("click", () => this.close());
    this.splitButton.addEventListener("click", () => this.openQuantity("SPLIT"));
    this.dropButton.addEventListener("click", () => this.openQuantity("DROP"));
    this.transferButton.addEventListener("click", () => this.transferSelected());
    this.transferQuantityButton.addEventListener("click", () => this.openQuantity("TRANSFER"));
    this.quantityCancel.addEventListener("click", () => this.closeQuantity());
    this.quantityConfirm.addEventListener("click", () => this.confirmQuantity());
    this.quantityInput.addEventListener("keydown", (event) => {
      if (event.code === "Enter") {
        event.preventDefault();
        this.confirmQuantity();
      }
    });
    this.root.addEventListener("pointerdown", (event) => event.stopPropagation());
    this.root.addEventListener("pointermove", (event) => event.stopPropagation());
    this.root.addEventListener("wheel", (event) => event.stopPropagation(), { passive: true });
    window.addEventListener("keydown", (event) => this.handleKey(event));
    this.render(store.snapshot());
  }

  get open(): boolean {
    return !this.root.hidden;
  }

  destroy(): void {
    this.physical.destroy();
    this.unsubscribe?.();
    this.unsubscribe = undefined;
  }

  profileRender(iterations = 100): Readonly<{
    iterations: number;
    playerSlots: number;
    containerSlots: number;
    totalMs: number;
    averageMs: number;
  }> {
    if (!Number.isSafeInteger(iterations) || iterations < 1 || iterations > 10_000) {
      throw new Error("Inventory render profile iterations must be between 1 and 10,000");
    }
    const snapshot = this.store.snapshot();
    const startedAt = performance.now();
    for (let iteration = 0; iteration < iterations; iteration += 1) this.render(snapshot);
    const totalMs = performance.now() - startedAt;
    return Object.freeze({
      iterations,
      playerSlots: snapshot.playerInventory?.slotCount ?? 0,
      containerSlots: snapshot.container?.state.slotCount ?? 0,
      totalMs,
      averageMs: totalMs / iterations,
    });
  }

  toggle(): void {
    if (this.open) this.close();
    else this.show();
  }

  show(): void {
    this.root.hidden = false;
    document.body.classList.add("inventory-modal-open");
    this.input.setInventoryOpen(true);
    this.input.reset();
    this.render(this.store.snapshot());
  }

  close(): void {
    if (!this.quantityDialog.hidden) {
      this.closeQuantity();
      return;
    }
    const container = this.store.snapshot().container;
    if (container) this.network.closeContainer(container.accessSessionId);
    this.root.hidden = true;
    document.body.classList.remove("inventory-modal-open");
    this.input.setInventoryOpen(false);
    this.input.reset();
    // Return keyboard input from the now-hidden panel to the gameplay HUD.
    this.mobileButton.focus({ preventScroll: true });
    this.selection = null;
  }

  private handleKey(event: KeyboardEvent): void {
    if (isEditable(event.target)) {
      if (event.code === "Escape" && !this.quantityDialog.hidden) {
        event.preventDefault();
        this.closeQuantity();
      }
      return;
    }
    if (event.code === "KeyI") {
      event.preventDefault();
      if (!event.repeat) this.toggle();
      return;
    }
    if (event.code !== "Escape" || !this.open) return;
    event.preventDefault();
    this.close();
  }

  private render(snapshot: ClientInventorySnapshot): void {
    const containerSession = snapshot.container?.accessSessionId ?? null;
    const shouldOpenForContainer = Boolean(
      containerSession
      && containerSession !== this.previousContainerSession
      && !this.open,
    );
    this.previousContainerSession = containerSession;
    if (shouldOpenForContainer) {
      this.show();
      return;
    }
    this.reconcileSelection(snapshot);
    const player = snapshot.playerInventory;
    this.playerMass.textContent = player
      ? `${formatMass(player.massGrams)} CARRIED`.toUpperCase()
      : "SYNCING";
    this.playerSlots.textContent = "PHYSICAL POCKETS";
    this.physical.render(snapshot, this.playerGrid);

    const container = snapshot.container;
    this.containerPane.hidden = !container;
    if (container) {
      this.containerTitle.textContent = "EMERGENCY CACHE";
      this.renderGrid(this.containerGrid, "CONTAINER", container.state.slots);
    } else {
      this.containerGrid.replaceChildren();
    }
    this.renderDetails(snapshot);
    this.physical.appendDetails(this.details, this.selectedStack(snapshot)?.stack ?? null, snapshot);
    const pending = snapshot.pendingRequests.size > 0 || !!snapshot.physicalPending || !!snapshot.physical?.action;
    this.root.dataset.pending = pending ? "true" : "false";
    const result = snapshot.lastResult;
    this.status.textContent = snapshot.physicalResult && snapshot.physicalResult.result !== "OK"
      ? resultLabel(snapshot.physicalResult.result) : result ? resultLabel(result.result) : snapshot.lastContainerCloseReason
      ? `CONTAINER ${snapshot.lastContainerCloseReason.replaceAll("_", " ")}`
      : "";
  }

  private renderGrid(root: HTMLElement, area: InventoryArea, slots: readonly InventorySlot[]): void {
    const fragment = document.createDocumentFragment();
    for (let index = 0; index < slots.length; index += 1) {
      const stack = slots[index];
      const definition = stack ? getItemDefinition(stack.definitionId) : undefined;
      const button = document.createElement("button");
      button.type = "button";
      button.className = "inventory-slot";
      button.dataset.area = area;
      button.dataset.slot = String(index);
      button.dataset.occupied = stack ? "true" : "false";
      button.dataset.selected = stack && this.selection?.stackId === stack.instanceId ? "true" : "false";
      button.draggable = Boolean(stack);
      button.setAttribute("aria-label", stack
        ? `${definition?.name ?? stack.definitionId}, quantity ${stack.quantity}, slot ${index + 1}`
        : `Empty ${area.toLowerCase()} slot ${index + 1}`);
      const icon = document.createElement("span");
      icon.className = "inventory-slot-icon";
      icon.textContent = stack ? itemIcon(definition) : "";
      button.append(icon);
      if (stack && stack.quantity > 1) {
        const quantity = document.createElement("b");
        quantity.textContent = String(stack.quantity);
        button.append(quantity);
      }
      if (stack?.durabilityCurrent !== undefined && definition?.durabilityMax) {
        const condition = document.createElement("i");
        condition.style.setProperty("--condition", String(stack.durabilityCurrent / definition.durabilityMax));
        button.append(condition);
      }
      button.addEventListener("click", () => this.activateSlot(area, index));
      button.addEventListener("dblclick", () => {
        if (stack && this.store.snapshot().container) {
          this.selection = { area, stackId: stack.instanceId };
          this.transferSelected();
        }
      });
      button.addEventListener("dragstart", (event) => {
        if (!stack) return event.preventDefault();
        this.selection = { area, stackId: stack.instanceId };
        event.dataTransfer?.setData("text/plain", `${area}:${stack.instanceId}`);
      });
      button.addEventListener("dragover", (event) => event.preventDefault());
      button.addEventListener("drop", (event) => {
        event.preventDefault();
        this.activateSlot(area, index);
      });
      fragment.append(button);
    }
    root.replaceChildren(fragment);
  }

  private activateSlot(area: InventoryArea, slotIndex: number): void {
    if (this.store.snapshot().pendingRequests.size > 0) return;
    const slots = area === "PLAYER"
      ? this.store.snapshot().playerInventory?.slots
      : this.store.snapshot().container?.state.slots;
    const targetStack = slots?.[slotIndex] ?? null;
    if (!this.selection) {
      if (targetStack) this.selection = { area, stackId: targetStack.instanceId };
      this.render(this.store.snapshot());
      return;
    }
    if (this.selection.area === area && this.selection.stackId === targetStack?.instanceId) {
      this.selection = null;
      this.render(this.store.snapshot());
      return;
    }
    if (this.selection.area !== area) {
      this.transferSelected();
      return;
    }
    if (area === "PLAYER") {
      const player = this.store.snapshot().playerInventory;
      if (!player) return;
      const requestId = this.requestId("move");
      if (!this.store.markPending(requestId, "MOVE")) return;
      this.network.moveInventoryStack(requestId, player.revision, this.selection.stackId, slotIndex);
      return;
    }
    if (targetStack) this.selection = { area, stackId: targetStack.instanceId };
    this.render(this.store.snapshot());
  }

  private renderDetails(snapshot: ClientInventorySnapshot): void {
    const selected = this.selectedStack(snapshot);
    if (!selected) {
      this.details.innerHTML = "<strong>SELECT AN ITEM</strong><span>Tap a stack to inspect or move it.</span>";
      this.setActions(null, snapshot);
      return;
    }
    const definition = getItemDefinition(selected.stack.definitionId);
    const durability = selected.stack.durabilityCurrent !== undefined && definition?.durabilityMax
      ? `<span>CONDITION ${selected.stack.durabilityCurrent} / ${definition.durabilityMax}</span>`
      : "";
    this.details.innerHTML = [
      `<strong>${escapeHtml(definition?.name ?? selected.stack.definitionId)}</strong>`,
      `<span>${escapeHtml(definition?.description ?? "Unknown item definition.")}</span>`,
      `<small>${definition?.category ?? "MISC"} · ${formatMass(stackMassGrams(selected.stack)).toUpperCase()} · QTY ${selected.stack.quantity}</small>`,
      durability,
    ].join("");
    this.setActions(selected.stack, snapshot);
  }

  private setActions(stack: ItemStack | null, snapshot: ClientInventorySnapshot): void {
    const pending = snapshot.pendingRequests.size > 0 || !!snapshot.physicalPending || !!snapshot.physical?.action;
    const playerSelected = this.selection?.area === "PLAYER";
    const hasContainer = Boolean(snapshot.container);
    this.splitButton.disabled = pending || !stack || !playerSelected || stack.quantity <= 1;
    this.dropButton.disabled = pending || !stack || !playerSelected;
    this.transferButton.disabled = pending || !stack || !hasContainer;
    this.transferQuantityButton.disabled = pending || !stack || !hasContainer || stack.quantity <= 1;
    this.transferButton.textContent = this.selection?.area === "CONTAINER" ? "TAKE" : "STORE";
  }

  private openQuantity(action: QuantityAction): void {
    const selected = this.selectedStack(this.store.snapshot());
    if (!selected) return;
    const maximum = action === "SPLIT" ? selected.stack.quantity - 1 : selected.stack.quantity;
    if (maximum < 1) return;
    this.quantityAction = action;
    this.quantityTitle.textContent = `${action} QUANTITY`;
    this.quantityInput.min = "1";
    this.quantityInput.max = String(maximum);
    this.quantityInput.value = String(action === "SPLIT" ? Math.max(1, Math.floor(selected.stack.quantity / 2)) : maximum);
    this.quantityDialog.hidden = false;
    this.quantityInput.focus();
    this.quantityInput.select();
  }

  private closeQuantity(): void {
    this.quantityDialog.hidden = true;
    this.quantityAction = null;
    this.quantityInput.blur();
  }

  private confirmQuantity(): void {
    const action = this.quantityAction;
    const selected = this.selectedStack(this.store.snapshot());
    const quantity = Number(this.quantityInput.value);
    if (!action || !selected || !Number.isSafeInteger(quantity) || quantity < 1) return;
    this.closeQuantity();
    if (action === "TRANSFER") return this.transferSelected(quantity);
    const inventory = this.store.snapshot().playerInventory;
    if (!inventory || selected.area !== "PLAYER") return;
    if (action === "DROP") {
      const requestId = this.requestId("drop");
      if (!this.store.markPending(requestId, "DROP")) return;
      this.network.dropInventoryStack(requestId, inventory.revision, selected.stack.instanceId, quantity);
      return;
    }
    const destinationSlot = this.store.snapshot().physical ? 0 : inventory.slots.indexOf(null);
    if (destinationSlot < 0) {
      this.status.textContent = "NO FREE SLOT";
      return;
    }
    const requestId = this.requestId("split");
    if (!this.store.markPending(requestId, "SPLIT")) return;
    this.network.splitInventoryStack(
      requestId,
      inventory.revision,
      selected.stack.instanceId,
      quantity,
      destinationSlot,
    );
  }

  private transferSelected(quantity?: number, destinationPocket?: PocketRef): void {
    const snapshot = this.store.snapshot();
    const selected = this.selectedStack(snapshot);
    const inventory = snapshot.playerInventory;
    const container = snapshot.container;
    if (!selected || !inventory || !container || snapshot.pendingRequests.size > 0) return;
    const requestId = this.requestId("transfer");
    if (!this.store.markPending(requestId, "TRANSFER")) return;
    this.selection = null;
    this.render(this.store.snapshot());
    const args = [
      requestId,
      container.accessSessionId,
      selected.area === "CONTAINER" ? "CONTAINER_TO_PLAYER" : "PLAYER_TO_CONTAINER",
      selected.stack.instanceId,
      quantity ?? selected.stack.quantity,
      inventory.revision,
      container.state.revision,
    ] as const;
    if (destinationPocket) this.network.transferContainerStack(...args, destinationPocket);
    else this.network.transferContainerStack(...args);
  }

  private selectedStack(snapshot: ClientInventorySnapshot): { area: InventoryArea; stack: ItemStack } | null {
    if (!this.selection) return null;
    const slots = this.selection.area === "PLAYER"
      ? snapshot.playerInventory?.slots
      : snapshot.container?.state.slots;
    const stack = slots?.find((candidate) => candidate?.instanceId === this.selection?.stackId) ?? null;
    return stack ? { area: this.selection.area, stack } : null;
  }

  private reconcileSelection(snapshot: ClientInventorySnapshot): void {
    if (this.selection && !this.selectedStack(snapshot)) this.selection = null;
  }

  private requestId(action: string): string {
    this.requestCounter += 1;
    const session = this.network.sessionId.replace(/[^A-Za-z0-9_-]/g, "").slice(-24) || "session";
    return `item_${action}_${session}_${this.requestCounter.toString(36)}`.slice(0, 64);
  }
}

function required<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Missing inventory UI element: ${selector}`);
  return element;
}

function resultLabel(result: string): string {
  if (result === "OK") return "UPDATED";
  if (result === "INVENTORY_FULL") return "NO ROOM";
  return result.replaceAll("_", " ");
}

function isEditable(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(target.closest("input, textarea, select, [contenteditable]:not([contenteditable='false'])"));
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}
